<template>
  <div class="">
    <div class="mt-2 lg:mt-0">
      <div>
        <div class="flex flex-col justify-between">
          <div class="text-2xl">Payment History</div>
          <div class="text-sm text-gray-500">
            Showing payments for the period 2021-08-03 to 2022-08-03
          </div>
          <div class="mt-2">
            <div class="flex flex-col md:items-end md:flex-row">
              <form
                @submit.prevent="getPaymentHistory"
                class="flex flex-col justify-end md:items-end md:flex-row"
              >
                <div class="flex">
                  <div class="w-full md:w-auto">
                    <p class="block text-gray-700 text-sm font-bold mb-1">
                      From
                    </p>
                    <div>
                      <Datepicker
                        :enableTimePicker="false"
                        class="appearance-none w-full py-2 rounded text-gray-800 leading-tight md:py-0 focus:outline-none focus:shadow-outline"
                        style="background: none; border: none"
                        v-model="startDate"
                      ></Datepicker>
                    </div>
                  </div>
                </div>
                <div class="flex mx-0 lg:mx-2 md:mx-0">
                  <div class="w-full md:w-auto">
                    <p class="block text-gray-700 text-sm font-bold mb-1">To</p>
                    <div>
                      <Datepicker
                        :enableTimePicker="false"
                        class="appearance-none w-full rounded py-2 text-gray-800 leading-tight md:py-0 md:px-3 focus:outline-none focus:shadow-outline"
                        style="background: none; border: none"
                        v-model="endDate"
                      ></Datepicker>
                    </div>
                  </div>
                </div>

                <div class="mr-3 mt-3 md:mt-0">
                  <button
                    :disabled="loading === true"
                    class="bg-[#8DC63F] hover:text-[#F58220] hover:bg-white hover:border-[#8DC63F] border transition-colors duration-300 text-white px-2 py-2 rounded-md text-sm w-full"
                  >
                    Fetch
                  </button>
                </div>
              </form>
              <button
                class="mx-7 bg-[#8DC63F] hover:text-[#F58220] hover:bg-white hover:border-[#8DC63F] border transition-colors duration-300 rounded-lg text-white px-2 py-3 text-xs mt-1"
                @click="sortByDate"
              >
                Sort by Date
              </button>
              <div class="flex flex-col-reverse md:flex-row">
                <div>
                  <div
                    class="mt-2 w-full flex items-center shadow text-gray-800 border pl-2 md:mt-0"
                  >
                    <font-awesome-icon icon="fa-solid fa-magnifying-glass" />
                    <input
                      v-model="filter"
                      :disabled="
                        $store.state.paymentLoading === true ||
                        $store.state.paymentHistoryData?.length === 0
                      "
                      class="appearance-none w-full rounded py-2 p-2 text-gray-800 leading-tight focus:outline-none focus:shadow-outline"
                      placeholder="search"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="mt-4 md:flex md:flex-row">
            <button
              @click="downloadFile()"
              :disabled="
                $store.state.paymentLoading === true ||
                $store.state.paymentHistoryData?.length === 0
              "
              class="bg-[#8DC63F] hover:text-[#F58220] hover:bg-white hover:border-[#8DC63F] border transition-colors duration-300 rounded-lg text-white px-3 py-1.5 cursor-pointer w-full md:w-auto"
            >
              Export to Excel
            </button>
            <button
              class="px-3 py-1.5 items-center bg-[#8DC63F] hover:text-[#F58220] hover:bg-white hover:border-[#8DC63F] border transition-colors duration-300 rounded-lg text-white mt-2 w-full md:mx-2 md:mt-0 md:w-auto cursor-pointer"
              @click.prevent="generateProofOfRemittance"
              :disabled="$store.state.paymentHistoryData?.length === 0"
            >
              <span>Generate Proof of Remittance Letter</span>
            </button>
          </div>
        </div>

        <div class="float-right p-4">
          <p class="text-red-500" v-if="$store.state.proofOfFundLoading">
            {{ $store.state.proofOfFundLoading?.message }}
          </p>
        </div>

        <div
          role="status"
          class="flex justify-center w-full my-6"
          v-if="$store.state.paymentLoading === true"
        >
          <LoadingSpinner />
        </div>

        <div
          class="overflow-x-auto w-full mb-4"
          v-if="$store.state.paymentLoading === false"
        >
          <TableComponent
            :columns="tableColumns"
            :data="displayedHistory"
            :renderCell="customRenderCell"
            :pageSize="20"
          >
            <template v-slot:actions="{ item }">
              <router-link :to="`payment-history-details/${item.no}`">
                <font-awesome-icon
                  class="hover:text-[#FF8200]"
                  style="cursor: pointer"
                  icon="eye"
                />
              </router-link>
            </template>
          </TableComponent>
          <div
            class="text-center text-[#8DC63F] my-3"
            v-if="
              $store.state.paymentHistoryData?.length === 0 &&
              !$store.state.paymentLoading
            "
          >
            No Transaction Available for the specified period
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from "@vue/reactivity";
import { onMounted } from "@vue/runtime-core";
import exportFromJSON from "export-from-json";
import TableComponent from "@/components/TableComponent.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import { useStore } from "vuex";
import { watch, watchEffect } from "vue";
import { useRouter } from "vue-router";

export default {
  components: {
    TableComponent,
    LoadingSpinner,
  },
  setup() {
    const paymentHistory = ref("");
    const loading = ref(false);
    const startDate = ref(
      new Date(new Date().setFullYear(new Date().getFullYear() - 1))
    );
    const store = useStore();
    const endDate = ref(new Date());
    const filter = ref("");
    const page = ref(1);
    const perPage = ref(20);
    const currentPage = ref(1);
    const remittanceLoading = ref(false);
    const error = ref("");
    const displayedHistory = ref([]);
    const router = useRouter();
    const tableColumns = ref([
      { key: "date", label: "Date", className: "px-2 py-2" },
      {
        key: "period_reference",
        label: "Period Reference",
        className: "px-2 py-2",
      },
      // { key: "entry_no", label: "Entry Number", className: "px-2 py-2" },
      { key: "amount", label: "Amount", className: "px-2 py-2" },

      { key: "actions", label: "Actions", className: "px-2 py-2", link: "no" },
    ]);

    const customRenderCell = (item, column) => {
      if (column.key === "description") {
        return item.description?.length > 20
          ? item.description.slice(0, 20)
          : item.description;
      }

      if (column.key === "date") {
        return item?.date.split(" ")[0];
      }

      // if (column.key === "period_reference") {
      //   return item?.period_reference.split(" ")[0];
      // }

      return item[column.key];
    };

    const downloadFile = () => {
      const data = store.state.paymentHistoryData;
      const fileName = "payment-history";
      const exportType = exportFromJSON.types.csv;

      if (data) exportFromJSON({ data, fileName, exportType });
    };

    const generateProofOfRemittance = async () => {
      const routePath = "/generate-remittance-proof";
      const url = router.resolve({ path: routePath }).href;
      window.open(url, "_blank");
      // proofOfFundLoading.value = false;
    };

    const startIndex = perPage.value * (page.value - 1);
    const endIndex = startIndex + perPage.value;

    watchEffect(() => {
      if (filter.value?.length > 1) {
        displayedHistory.value = store.state.paymentHistoryData
          .filter((paymentHistory) => {
            return paymentHistory.period_reference
              .toLowerCase()
              .includes(filter.value.toLowerCase());
          })
          ?.sort((a, b) => new Date(b.date) - new Date(a.date));
      } else {
        displayedHistory.value = store.state?.paymentHistoryData?.sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );
      }
      console.log(displayedHistory.value.length, "count");
    });

    watch(startDate, (newValue) => {
      store.dispatch("changeStartDate", newValue);
    });

    watch(endDate, (newValue) => {
      store.dispatch("changeEndDate", newValue);
    });

    const sortByDate = () => {
      const sortedHistoryCopy = [...displayedHistory.value];
      const sortedHistory = sortedHistoryCopy.reverse();
      displayedHistory.value = sortedHistory;
    };

    const getPaymentHistory = async () => {
      store
        .dispatch("getRemittance", {
          start_date: startDate.value,
          end_date: endDate.value,
        })
        .then(() => (displayedHistory.value = store.state.paymentHistoryData));
      console.log(displayedHistory.value, "count");
    };

    onMounted(() => {
      getPaymentHistory();
    });

    return {
      paymentHistory,
      loading,
      startDate,
      endDate,
      getPaymentHistory,
      displayedHistory,
      filter,
      page,
      perPage,
      currentPage,
      downloadFile,
      generateProofOfRemittance,
      remittanceLoading,
      error,
      tableColumns,
      customRenderCell,
      sortByDate,
    };
  },
};
</script>

<style scoped>
tbody tr {
  background-color: #fff;
  border-bottom: 1px solid #e5e7eb;
}
</style>

<style></style>
